import { McpToolName } from '@systemeio/ai-chat-types'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

const contactModules = [RequestModuleEnum.Contacts]
const tagModules = [RequestModuleEnum.Tags]
const automationRuleModules = [RequestModuleEnum.AutomationRule]
const newsletterModules = [RequestModuleEnum.Newsletters]
const campaignModules = [RequestModuleEnum.Campaign]
const campaignStepModules = [RequestModuleEnum.CampaignStep]
const funnelStepModules = [RequestModuleEnum.FunnelsStep]
// Bookings display event fields
const calendarEventModules = [RequestModuleEnum.Calendar, RequestModuleEnum.CalendarBookings]
// Event responses embed the global availability
const calendarAvailabilityModules = [
  RequestModuleEnum.AvailabilityBookings,
  RequestModuleEnum.Calendar,
]
const smsTemplateModules = [RequestModuleEnum.SmsTemplates]
const communityModules = [RequestModuleEnum.Community]
const classicCourseModules = [RequestModuleEnum.MembershipCourse]
const courseStatusModules = [
  RequestModuleEnum.MembershipCourse,
  RequestModuleEnum.MembershipLightCourse,
]
// Module and lecture tools serve both classic and light courses
const courseModuleModules = [
  RequestModuleEnum.MembershipCourseCurriculum,
  RequestModuleEnum.MembershipLightCourseCurriculum,
]
const lectureModules = [...courseModuleModules, RequestModuleEnum.MembershipCourseLecture]
const couponModules = [RequestModuleEnum.Coupons]
const pricePlanModules = [RequestModuleEnum.PricePlans]
const digitalProductModules = [RequestModuleEnum.DigitalProducts]
const physicalProductModules = [RequestModuleEnum.PhysicalProducts]

/**
 * SWR modules to revalidate after a successful MCP tool call from the AI chat.
 * Read-only tools are not listed. Workflows, workflow steps, automation emails and sale settings
 * are cached under plain string keys, so they cannot be revalidated by module yet.
 */
export const mcpToolRevalidationModules: Partial<Record<McpToolName, RequestModuleEnum[]>> = {
  [McpToolName.AssignContactTag]: contactModules,
  [McpToolName.CreateContact]: contactModules,
  [McpToolName.RemoveContact]: contactModules,
  [McpToolName.RemoveContactTag]: contactModules,
  [McpToolName.UpdateContact]: contactModules,

  [McpToolName.CreateTag]: tagModules,
  [McpToolName.RemoveTag]: tagModules,
  [McpToolName.UpdateTag]: tagModules,

  [McpToolName.CreateAutomationRule]: automationRuleModules,
  [McpToolName.DeleteAutomationRule]: automationRuleModules,
  [McpToolName.UpdateAutomationRule]: automationRuleModules,

  [McpToolName.CreateNewsletter]: newsletterModules,
  [McpToolName.DeleteNewsletter]: newsletterModules,
  [McpToolName.SendNewsletter]: newsletterModules,
  [McpToolName.SetNewsletterExcludedTags]: newsletterModules,
  [McpToolName.SetNewsletterIncludedTags]: newsletterModules,
  [McpToolName.SetNewsletterSchedule]: newsletterModules,
  [McpToolName.UpdateNewsletter]: newsletterModules,

  [McpToolName.CreateCampaign]: campaignModules,
  [McpToolName.UpdateCampaign]: campaignModules,
  [McpToolName.DeleteCampaign]: [RequestModuleEnum.Campaign, RequestModuleEnum.CampaignStep],
  [McpToolName.CreateCampaignStep]: campaignStepModules,
  [McpToolName.DeleteCampaignStep]: campaignStepModules,
  [McpToolName.UpdateCampaignStep]: campaignStepModules,

  [McpToolName.CreateFunnel]: [RequestModuleEnum.Funnels],
  [McpToolName.CreateFunnelStep]: funnelStepModules,
  [McpToolName.MoveFunnelStep]: funnelStepModules,
  [McpToolName.SaveContent]: funnelStepModules,
  [McpToolName.SaveFunnelPageContent]: funnelStepModules,
  [McpToolName.SavePageContentFromTemplate]: funnelStepModules,
  [McpToolName.UpdatePageContent]: funnelStepModules,

  [McpToolName.CreateCalendarEvent]: calendarEventModules,
  [McpToolName.UpdateCalendarEvent]: calendarEventModules,
  [McpToolName.DeleteCalendarEvent]: calendarEventModules,
  [McpToolName.CreateCalendarAvailability]: calendarAvailabilityModules,
  [McpToolName.UpdateCalendarAvailability]: calendarAvailabilityModules,

  [McpToolName.CreateSmsTemplate]: smsTemplateModules,
  [McpToolName.DeleteSmsTemplate]: smsTemplateModules,
  [McpToolName.UpdateSmsTemplate]: smsTemplateModules,

  [McpToolName.CreateCommunity]: communityModules,
  [McpToolName.DeleteCommunity]: communityModules,
  [McpToolName.UpdateCommunity]: communityModules,

  [McpToolName.CreateClassicCourse]: classicCourseModules,
  [McpToolName.UpdateClassicCourse]: classicCourseModules,
  // The course status endpoints serve both classic and light courses
  [McpToolName.ActivateCourse]: courseStatusModules,
  [McpToolName.DeactivateCourse]: courseStatusModules,
  [McpToolName.DeleteCourse]: [
    ...courseStatusModules,
    RequestModuleEnum.MembershipCourseCurriculum,
    RequestModuleEnum.MembershipLightCourseCurriculum,
    RequestModuleEnum.MembershipCourseLecture,
  ],
  [McpToolName.CreateCourseModule]: courseModuleModules,
  [McpToolName.UpdateCourseModule]: courseModuleModules,
  [McpToolName.DeleteCourseModule]: lectureModules,
  [McpToolName.ActivateLecture]: lectureModules,
  [McpToolName.CreateClassicLecture]: lectureModules,
  [McpToolName.DeactivateLecture]: lectureModules,
  [McpToolName.DeleteLecture]: lectureModules,
  [McpToolName.UpdateClassicLecture]: lectureModules,

  [McpToolName.CreateCoupon]: couponModules,
  [McpToolName.DeleteCoupon]: couponModules,
  [McpToolName.UpdateCoupon]: couponModules,

  [McpToolName.CreatePricePlan]: pricePlanModules,
  [McpToolName.DeletePricePlan]: pricePlanModules,
  [McpToolName.UpdatePricePlan]: pricePlanModules,

  [McpToolName.CreateDigitalProduct]: digitalProductModules,
  [McpToolName.DeleteDigitalProduct]: digitalProductModules,
  [McpToolName.UpdateDigitalProduct]: digitalProductModules,
  // Offers are shown in the funnel step offer form, whose key includes the digital products module
  [McpToolName.CreateOffer]: digitalProductModules,
  [McpToolName.UpdateOffer]: digitalProductModules,

  [McpToolName.CreatePhysicalProduct]: physicalProductModules,
  [McpToolName.DeletePhysicalProduct]: physicalProductModules,
  [McpToolName.UpdatePhysicalProduct]: physicalProductModules,
}
